import {
  buildGraph,
  type ConnectionInput,
  type Edge,
  type Graph,
  type Vertex,
  type StationInput,
  type TransitionInput,
} from '@metro/graph'
import { DEFAULT_CONFIG, RoutingError, findRoutes, type Route } from '@metro/routing'
import { buildStationComplexIndex, type ComplexStation } from './stationComplexIndex'
import { buildStationComplexStops } from './stationComplexStops'

/**
 * Extra seconds the fewest-changes search pass charges per change
 * (`RouteOptions.transferPenaltySec`). Cross-platform constant — iOS
 * `OfflineRouteSearch.fewestChangesPenaltySec`, Android
 * `RoutingAdapter.FEWEST_CHANGES_PENALTY_SEC`.
 */
export const FEWEST_CHANGES_PENALTY_SEC = 1200

/**
 * What the offline search is built from — plain data, so the same search can
 * be built inside a Web Worker (`offlineRouteWorker`) from a structured clone.
 */
export interface OfflineRouteInputs {
  readonly stations: readonly StationInput[]
  readonly connections: readonly ConnectionInput[]
  readonly transitions: readonly TransitionInput[]
  /** The stops' slugs and NaPTANs, for the destination complexes. */
  readonly complexStations: readonly ComplexStation[]
}

/**
 * The on-device route search: three passes over one graph, merged —
 * - standard: the K fastest paths;
 * - pure-transit: the same without walk edges, so the list always carries a
 *   no-walking option when the map has one;
 * - fewest changes (TASK-986): the time-ordered K shortest paths of the v2
 *   map are near-identical multi-change permutations; ranking every change as
 *   `FEWEST_CHANGES_PENALTY_SEC` longer surfaces the journey a rider would
 *   pick (Tooting Broadway → Heathrow T5: Northern + Elizabeth at Tottenham
 *   Court Road, Northern + Piccadilly). Ten minutes was not enough once
 *   Paddington's interchanges were drawn: its two-change permutations filled
 *   the pass's K budget.
 * Each pass truncates a trailing transfer inside the destination complex.
 * iOS twin: `OfflineRouteSearch`.
 *
 * @throws {RoutingError} `'NoPath'` when every pass comes up empty.
 */
export type OfflineRouteSearch = (fromStationId: string, toStationId: string) => Route[]

export function createOfflineRouteSearch(
  graph: Graph,
  complexNaptansOf: (stopId: string) => ReadonlySet<string>,
  complexStopsOf: (stopId: string) => readonly string[] = (stopId) => [stopId],
): OfflineRouteSearch {
  return (fromID, toID) => {
    const edges = graph.allEdges()
    // The picked stop, or — when the graph lacks it (a line that does not
    // run at night) — the first stop of its complex that the graph has.
    const firstOf = (stopId: string, at: (e: Edge) => Vertex) => {
      for (const id of complexStopsOf(stopId)) {
        const found = edges.find((e) => at(e).id === id)
        if (found) return at(found)
      }
      return undefined
    }
    const src = firstOf(fromID, (e) => e.from)
    const dst = firstOf(toID, (e) => e.to)
    if (!src || !dst) throw new RoutingError('NoPath')
    const dstNaptanIds = complexNaptansOf(toID)
    const passes = [
      { config: DEFAULT_CONFIG, dstNaptanIds },
      { config: DEFAULT_CONFIG, dstNaptanIds, excludeWalkTransfers: true },
      { config: DEFAULT_CONFIG, dstNaptanIds, transferPenaltySec: FEWEST_CHANGES_PENALTY_SEC },
    ]
    const merged: Route[] = []
    for (const options of passes) {
      try {
        merged.push(...findRoutes(graph, src, dst, options))
      } catch (err) {
        // Another pass may still succeed.
        if (!(err instanceof RoutingError)) throw err
      }
    }
    if (merged.length === 0) throw new RoutingError('NoPath')
    return merged
  }
}

/** The search over a graph built from `inputs` — what the worker runs. */
export function offlineRouteSearchFrom(inputs: OfflineRouteInputs): OfflineRouteSearch {
  const complex = { stations: inputs.complexStations }
  return createOfflineRouteSearch(
    buildGraph(inputs),
    buildStationComplexIndex(complex),
    buildStationComplexStops(complex),
  )
}
